/**
 * ログイン後の画面(docs/20 §5)。左ナビ = 科目 / キャラクター / アイテム / ガチャ / タイピング /
 * 問題集 / 修正メモ / 公開 / 権限。
 *
 * 科目〜ガチャ(段階B)は下書き(cmsDrafts)を読み、cmsSaveDrafts で保存する。4画面で同じ下書きを共有する。
 * タイピング・問題集・修正メモは閲覧(既存画面)、公開・権限は段階A。権限は owner にだけ出す。
 */
import { useEffect, useMemo, useState } from 'react';
import { canEdit, canManageRoles, ROLE_LABELS, type AdminSession } from '../services/adminAuth';
import {
  LANGUAGES,
  LANGUAGE_LABELS,
  type Language,
  type LanguageContent,
} from '../services/content';
import type { ReadyBootstrap } from '../services/bootstrap';
import { BUNDLED_RELEASE_ID, type AdminContentSource } from '../services/contentSource';
import { ContentRequestService } from '../services/requests';
import { CharactersPanel } from './catalog/CharactersPanel';
import { LoadGate, useCatalogStore } from './catalog/common';
import { GachasPanel } from './catalog/GachasPanel';
import { ItemsPanel } from './catalog/ItemsPanel';
import { SubjectsPanel } from './catalog/SubjectsPanel';
import { PublishPanel } from './PublishPanel';
import { RequestDialog, type RequestTarget } from './RequestDialog';
import { RequestsPanel } from './RequestsPanel';
import { RolesPanel } from './RolesPanel';
import { QuizBrowser } from './QuizBrowser';
import { StageBrowser } from './StageBrowser';
import { WordBrowser } from './WordBrowser';

export type Section =
  | 'subjects'
  | 'characters'
  | 'items'
  | 'gachas'
  | 'typing'
  | 'quiz'
  | 'requests'
  | 'publish'
  | 'roles';

/** 左ナビの並び(docs/20 §5 の表の順)。 */
export const SECTIONS: readonly Section[] = [
  'subjects',
  'characters',
  'items',
  'gachas',
  'typing',
  'quiz',
  'requests',
  'publish',
  'roles',
];

export const SECTION_LABELS: Readonly<Record<Section, string>> = {
  subjects: '科目',
  characters: 'キャラクター',
  items: 'アイテム',
  gachas: 'ガチャ',
  typing: 'タイピング',
  quiz: '問題集',
  requests: '修正メモ',
  publish: '公開',
  roles: '権限',
};

/** 下書き(科目・キャラクター・アイテム・ガチャ)を編集する区分。 */
const CATALOG_SECTIONS: readonly Section[] = ['subjects', 'characters', 'items', 'gachas'];

type TypingTab = 'stages' | 'words';

const TYPING_TAB_LABELS: Readonly<Record<TypingTab, string>> = {
  stages: 'ステージ',
  words: '単語(横断)',
};

interface Props {
  readonly boot: ReadyBootstrap;
  readonly session: AdminSession;
}

/** その role で見える区分(権限は owner だけ)。 */
export function visibleSections(session: Pick<AdminSession, 'role'>): readonly Section[] {
  return SECTIONS.filter((section) => section !== 'roles' || canManageRoles(session.role));
}

export function Console({ boot, session }: Props) {
  const [section, setSection] = useState<Section>('typing');
  const [typingTab, setTypingTab] = useState<TypingTab>('stages');
  const [language, setLanguage] = useState<Language>('japanese');
  const [content, setContent] = useState<LanguageContent | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [source, setSource] = useState<AdminContentSource | null>(null);
  const [target, setTarget] = useState<RequestTarget | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  const requests = useMemo(
    () => new ContentRequestService(boot.firebase.db, session.uid),
    [boot.firebase.db, session.uid],
  );
  const sections = visibleSections(session);
  const catalog = useCatalogStore(
    boot.catalogReader,
    boot.catalogSaver,
    CATALOG_SECTIONS.includes(section),
  );
  const editable = canEdit(session.role);

  useEffect(() => {
    let cancelled = false;
    void boot.contentSource.then((value) => {
      if (!cancelled) setSource(value);
    });
    return () => {
      cancelled = true;
    };
  }, [boot.contentSource]);

  useEffect(() => {
    if (section !== 'typing') return undefined;
    let cancelled = false;
    setContent(null);
    setContentError(null);
    boot.content
      .load(language)
      .then((loaded) => {
        if (!cancelled) setContent(loaded);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setContentError(String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, [boot.content, language, section]);

  function renderSection() {
    if (CATALOG_SECTIONS.includes(section)) {
      return (
        <LoadGate store={catalog}>
          {section === 'subjects' && <SubjectsPanel store={catalog} editable={editable} />}
          {section === 'characters' && (
            <CharactersPanel store={catalog} editable={editable} contentBase={boot.contentBase} />
          )}
          {section === 'items' && (
            <ItemsPanel store={catalog} editable={editable} contentBase={boot.contentBase} />
          )}
          {section === 'gachas' && <GachasPanel store={catalog} editable={editable} />}
        </LoadGate>
      );
    }
    switch (section) {
      case 'publish':
        return (
          <PublishPanel
            cms={boot.cms}
            reader={boot.cmsReader}
            role={session.role}
            webAppUrl={boot.webAppUrl}
          />
        );
      case 'roles':
        return canManageRoles(session.role) ? (
          <RolesPanel cms={boot.cms} reader={boot.cmsReader} />
        ) : null;
      case 'requests':
        return <RequestsPanel service={requests} reloadKey={reloadKey} />;
      case 'quiz':
        return (
          <QuizBrowser loader={boot.quiz} assetsBase={boot.assetsBase} onRequest={setTarget} />
        );
      default:
        return (
          <>
            <div className="subbar">
              <div className="tabs">
                {(Object.keys(TYPING_TAB_LABELS) as TypingTab[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    className={`tab${typingTab === value ? ' tab--active' : ''}`}
                    onClick={() => {
                      setTypingTab(value);
                    }}
                  >
                    {TYPING_TAB_LABELS[value]}
                  </button>
                ))}
              </div>
              <select
                className="select select--inline"
                aria-label="言語"
                value={language}
                onChange={(event) => {
                  setLanguage(event.target.value as Language);
                }}
              >
                {LANGUAGES.map((value) => (
                  <option key={value} value={value}>
                    {LANGUAGE_LABELS[value]}
                  </option>
                ))}
              </select>
            </div>
            {contentError !== null && (
              <div className="notice notice--error" role="alert">
                コンテンツを読み込めませんでした: {contentError}
              </div>
            )}
            {content === null ? (
              <div className="pane">
                <p className="muted">コンテンツを読み込み中…</p>
              </div>
            ) : typingTab === 'stages' ? (
              <StageBrowser content={content} assetsBase={boot.assetsBase} onRequest={setTarget} />
            ) : (
              <WordBrowser content={content} assetsBase={boot.assetsBase} onRequest={setTarget} />
            )}
          </>
        );
    }
  }

  return (
    <div className="shell">
      <header className="topbar">
        <h1 className="topbar__title">PaLTown 管理画面</h1>
        <span className="muted" data-testid="content-source">
          表示中のデータ:{' '}
          {source === null
            ? '確認中…'
            : source.releaseId === BUNDLED_RELEASE_ID
              ? '同梱版(bundled-v1)'
              : `公開版 ${source.releaseId}`}
        </span>
        <div className="topbar__spacer" />
        <span className="muted mono">
          {session.email ?? session.uid} / {ROLE_LABELS[session.role]}
          {session.legacy && '(移行期間の admins 登録)'}
          {boot.usingEmulator && ' (エミュレータ)'}
        </span>
        <button
          className="button button--small"
          type="button"
          onClick={() => {
            void boot.auth.signOut();
          }}
        >
          ログアウト
        </button>
      </header>

      {toast !== null && (
        <div className="notice notice--ok" role="status">
          {toast}
        </div>
      )}

      <div className="console">
        <nav className="sidenav" aria-label="管理メニュー">
          {sections.map((value) => (
            <button
              key={value}
              type="button"
              className={`sidenav__item${section === value ? ' sidenav__item--active' : ''}`}
              aria-current={section === value ? 'page' : undefined}
              onClick={() => {
                setSection(value);
              }}
            >
              {SECTION_LABELS[value]}
            </button>
          ))}
        </nav>
        <main className="console__main">{renderSection()}</main>
      </div>

      {target !== null && (
        <RequestDialog
          target={target}
          service={requests}
          onClose={() => {
            setTarget(null);
          }}
          onCreated={() => {
            setTarget(null);
            setReloadKey((value) => value + 1);
            setToast('修正メモを保存しました。「修正メモ」で確認できます。');
            window.setTimeout(() => {
              setToast(null);
            }, 4000);
          }}
        />
      )}
    </div>
  );
}
